import { supabase } from './supabase';
import { getCachedSignedUrl } from './imageCacheService';

/**
 * Upload file to Supabase Storage
 */
export async function uploadFile(
  bucket: string,
  path: string,
  file: File,
  options?: {
    cacheControl?: string;
    contentType?: string;
    upsert?: boolean;
  }
): Promise<{ path: string; publicUrl: string }> {
  const { data, error } = await supabase.storage
    .from(bucket)
    .upload(path, file, {
      cacheControl: options?.cacheControl || '3600',
      contentType: options?.contentType || file.type,
      upsert: options?.upsert || false,
    });

  if (error) {
    console.error(`Upload Error [${bucket}]:`, error);
    throw error;
  }

  const { data: urlData } = supabase.storage
    .from(bucket)
    .getPublicUrl(data.path);

  return {
    path: data.path,
    publicUrl: urlData.publicUrl,
  };
}

/**
 * Get public URL for file (works for both public and private buckets)
 * For private buckets, the URL will use /object/authenticated/ endpoint
 * and requires auth headers (automatically handled by Supabase client)
 */
export function getPublicUrl(bucket: string, path: string): string {
  const { data } = supabase.storage.from(bucket).getPublicUrl(path);
  return data.publicUrl;
}

/**
 * Convert authenticated URL to signed URL for display in img tags
 * For private buckets, signed URLs work without auth headers.
 * Uses image caching to avoid regenerating signed URLs.
 */
export async function getSignedUrlFromAuthenticatedUrl(
  authenticatedUrl: string,
  expiresIn: number = 3600
): Promise<string> {
  // Extract bucket and path from authenticated URL
  // Format: https://project.supabase.co/storage/v1/object/authenticated/bucket/path
  const match = authenticatedUrl.match(/\/storage\/v1\/object\/authenticated\/([^/]+)\/(.+)$/);
  
  if (!match) {
    // If not an authenticated URL, return as is
    return authenticatedUrl;
  }
  
  const [, bucket, path] = match;
  
  // Use cached signed URL or generate new one
  return getCachedSignedUrl(
    authenticatedUrl,
    async () => {
      // Generate signed URL
      const { data, error } = await supabase.storage
        .from(bucket)
        .createSignedUrl(path, expiresIn);
      
      if (error) {
        console.error('Error creating signed URL:', error);
        // Fallback to original URL
        return authenticatedUrl;
      }
      
      return data.signedUrl;
    },
    expiresIn
  );
}

/**
 * Get signed URL (for private files)
 */
export async function getSignedUrl(
  bucket: string,
  path: string,
  expiresIn: number = 3600
): Promise<string> {
  const { data, error } = await supabase.storage
    .from(bucket)
    .createSignedUrl(path, expiresIn);

  if (error) {
    console.error(`Signed URL Error [${bucket}]:`, error);
    throw error;
  }
  
  return data.signedUrl;
}

/**
 * Delete file from storage
 */
export async function deleteFile(bucket: string, path: string): Promise<void> {
  const { error } = await supabase.storage.from(bucket).remove([path]);

  if (error) {
    console.error(`Delete File Error [${bucket}]:`, error);
    throw error;
  }
}

/**
 * List files in bucket
 */
export async function listFiles(
  bucket: string,
  folder?: string,
  options?: {
    limit?: number;
    offset?: number;
    sortBy?: { column: string; order?: 'asc' | 'desc' };
  }
): Promise<string[]> {
  const { data, error } = await supabase.storage
    .from(bucket)
    .list(folder, options);

  if (error) {
    console.error(`List Files Error [${bucket}]:`, error);
    throw error;
  }
  
  return data.map((file) => file.name);
}

